import { useParams, Link } from 'react-router-dom';
import { movies, categories } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((m) => m.id === id);

  if (!movie) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-foreground-500 text-2xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-300 mb-2">电影未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">该电影可能已下架或链接不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-950 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.id !== movie.id && m.categories.some((c) => movie.categories.includes(c)))
    .slice(0, 6);

  const movieCatSlug = categories.find((c) => c.name === movie.category)?.slug || 'all';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: movie.category, href: `/category/${movieCatSlug}` },
    { label: movie.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    alternateName: movie.titleEn,
    description: movie.description,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    countryOfOrigin: movie.region,
    duration: movie.duration,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: 10,
      ratingCount: Math.floor(movie.viewCount / 1000),
    },
  };

  return (
    <>
      <SEO
        title={`${movie.title} - 高清在线观看 | 8090影院`}
        description={`${movie.title}(${movie.titleEn})${movie.year}年${movie.region}${movie.category}，导演${movie.director}，主演${movie.actors.join('、')}。8090影院免费高清在线观看，${movie.description.slice(0, 80)}...`}
        keywords={`${movie.title},${movie.titleEn},${movie.category},${movie.region}电影,${movie.year},${movie.director},${movie.actors.slice(0, 3).join(',')},8090影院,高清电影在线观看`}
        ogImage={movie.posterLarge}
        ogType="video.movie"
        structuredData={articleSchema}
      />

      <div className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Movie Detail Header */}
            <div className="flex flex-col md:flex-row gap-8 mb-10">
              <div className="w-full md:w-[300px] shrink-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} 海报 - 高清电影在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {movie.isNew && (
                    <span className="px-2.5 py-1 bg-accent-500 text-foreground-50 text-xs font-medium rounded-md">NEW</span>
                  )}
                  {movie.isHot && (
                    <span className="px-2.5 py-1 bg-primary-500 text-background-950 text-xs font-medium rounded-md">热播</span>
                  )}
                  <Link
                    to={`/category/${movieCatSlug}`}
                    className="px-2.5 py-1 bg-background-100 text-foreground-400 text-xs rounded-md hover:text-primary-500 transition-colors"
                  >
                    {movie.category}
                  </Link>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-100 font-heading mb-2">{movie.title}</h1>
                <p className="text-sm text-foreground-500 font-heading mb-4">{movie.titleEn}</p>

                <div className="flex items-center gap-6 mb-6 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-star-fill text-primary-500 text-lg"></i>
                    </div>
                    <span className="text-2xl font-bold text-primary-500">{movie.rating}</span>
                    <span className="text-sm text-foreground-500">/ 10</span>
                  </div>
                  <span className="text-sm text-foreground-400">{movie.year}</span>
                  <span className="text-sm text-foreground-400">{movie.duration}</span>
                  <span className="text-sm text-foreground-400">{movie.language}</span>
                  <span className="text-sm text-foreground-400">{(movie.viewCount / 10000).toFixed(0)}万次观看</span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 shrink-0 w-14">导演</span>
                    <span className="text-sm text-foreground-200">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 shrink-0 w-14">主演</span>
                    <span className="text-sm text-foreground-200">{movie.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 shrink-0 w-14">地区</span>
                    <span className="text-sm text-foreground-200">{movie.region}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 shrink-0 w-14">类型</span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {movie.categories.map((cat) => {
                        const catSlug = categories.find((c) => c.name === cat)?.slug || 'all';
                        return (
                          <Link
                            key={cat}
                            to={`/category/${catSlug}`}
                            className="px-2.5 py-1 bg-background-100 text-foreground-400 text-xs rounded-md hover:text-primary-500 transition-colors"
                          >
                            {cat}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-foreground-500 shrink-0 w-14">上映</span>
                    <span className="text-sm text-foreground-200">{movie.releaseDate}</span>
                  </div>
                </div>

                <Link
                  to={`/category/${movieCatSlug}`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-950 font-semibold rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-fill"></i>
                  </div>
                  立即观看
                </Link>
              </div>
            </div>

            {/* Movie Description */}
            <section className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-5 bg-primary-500 rounded-full" />
                <h2 className="text-lg font-bold text-foreground-100 font-heading">剧情简介</h2>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/50">
                <p className="text-sm text-foreground-300 leading-relaxed">{movie.description}</p>
              </div>
            </section>

            {/* Related Movies */}
            {relatedMovies.length > 0 && (
              <section className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-5 bg-accent-500 rounded-full" />
                  <h2 className="text-lg font-bold text-foreground-100 font-heading">相关推荐</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </div>
    </>
  );
}